/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

/**
 * Performs a bare `XMLHttpRequest` GET and adapts it to a minimal fetch-like
 * response shape (`ok`, `status`, `json()`), used for the public listing which
 * runs without the authenticated API client. Rejects on network error.
 *
 * @param url - The absolute URL to GET.
 * @returns A promise resolving to a fetch-like response.
 */
export function publicFetch(url: string): Promise<{ ok: boolean; status: number; json: () => Promise<any> }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.onload = () => resolve({
      ok: xhr.status >= 200 && xhr.status < 300,
      status: xhr.status,
      json: () => Promise.resolve(JSON.parse(xhr.responseText)),
    });
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send();
  });
}

export type { PublicPropertyView as PublicProperty } from '@/shared/utils/to-public-property-view';
export { toPublicPropertyView } from '@/shared/utils/to-public-property-view';

/**
 * Builds a map from raw property-category codes to their localized display
 * labels, used to translate the property's category for public rendering.
 *
 * @param t - Translation function for the public-property namespace.
 * @returns A record mapping category code to localized label.
 */
/**
 * Builds the localized category-label map (Chacara, Apartamento, Casa, etc.)
 * used to translate a property's raw category code for display.
 *
 * @param t - Translation function for the category labels.
 * @returns A map from raw category code to localized display label.
 */
export function getCategoryDisplay(t: (key: string) => string): Record<string, string> {
  return {
    Chacara: t('categories.Chacara'),
    Apartamento: t('categories.Apartamento'),
    Casa: t('categories.Casa'),
    Terreno: t('categories.Terreno'),
    Comercial: t('categories.Comercial'),
    Fazenda: t('categories.Fazenda'),
  };
}

/**
 * Formats a numeric value as Brazilian Real currency (pt-BR, no decimals).
 *
 * @param value - The amount to format.
 * @returns The formatted BRL currency string.
 */
/**
 * Formats a numeric value as a whole-real Brazilian currency string (BRL,
 * pt-BR locale, no fractional digits).
 *
 * @param value - The amount in reais.
 * @returns The formatted currency string.
 */
export function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}
